import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Search, X, CheckCircle, BanknoteIcon, Download, Upload, FileSpreadsheet, Receipt, AlertCircle, Printer, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Payments = () => {
  const { user, hasPermission } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isDepositor = user?.username?.toLowerCase() === 'alma';
  const canConfirmDeposit = isAdmin || isDepositor || hasPermission('confirm_deposits');
  const canEditPayments = isAdmin || hasPermission('edit_payments');
  const canDeletePayments = isAdmin || hasPermission('delete_payments');
  const [payments, setPayments] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [subscriberSearch, setSubscriberSearch] = useState('');
  const [showSubscriberDropdown, setShowSubscriberDropdown] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [unpaidBills, setUnpaidBills] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [loadingBills, setLoadingBills] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const receiptRef = useRef(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    subscriber_id: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    received_by: '',
    deposited_by: '',
    notes: '',
    bill_number: ''
  });

  const fetchData = async () => {
    try {
      const [paymentsRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/payments`),
        axios.get(`${API}/subscribers`)
      ]);
      setPayments(paymentsRes.data);
      setSubscribers(subscribersRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let paymentResult = null;
      
      if (editingPayment) {
        await axios.put(`${API}/payments/${editingPayment.id}`, formData);
        toast.success('Payment updated successfully');
      } else if (selectedBill && formData.bill_number) {
        // Pay using the bill number for accurate tracking
        const response = await axios.post(`${API}/subscriber-bills/pay`, {
          bill_number: formData.bill_number,
          amount: parseFloat(formData.amount),
          payment_date: formData.payment_date,
          payment_method: formData.payment_method,
          received_by: formData.received_by,
          deposited_by: formData.deposited_by,
          notes: formData.notes
        });
        paymentResult = response.data;
        toast.success(`Payment recorded for ${formData.bill_number}`);
        
        // Prepare receipt data
        setReceiptData({
          receiptNumber: `RCP-${Date.now().toString(36).toUpperCase()}`,
          billNumber: formData.bill_number,
          subscriberName: selectedSubscriber?.name || 'N/A',
          subscriberPlan: selectedSubscriber?.plan || 'N/A',
          billingMonth: selectedBill?.billing_month_name || 'N/A',
          amount: parseFloat(formData.amount),
          paymentDate: formData.payment_date,
          paymentMethod: formData.payment_method,
          receivedBy: formData.received_by || user?.name || 'Admin',
          notes: formData.notes,
          newStatus: paymentResult?.new_status || 'paid',
          remainingBalance: paymentResult?.balance || 0
        });
        setShowReceipt(true);
      } else {
        // Regular payment without bill tracking
        await axios.post(`${API}/payments`, formData);
        toast.success('Payment recorded successfully');
        
        // Prepare receipt data for regular payment
        setReceiptData({
          receiptNumber: `RCP-${Date.now().toString(36).toUpperCase()}`,
          billNumber: 'N/A',
          subscriberName: selectedSubscriber?.name || 'N/A',
          subscriberPlan: selectedSubscriber?.plan || 'N/A',
          billingMonth: 'N/A',
          amount: parseFloat(formData.amount),
          paymentDate: formData.payment_date,
          paymentMethod: formData.payment_method,
          receivedBy: formData.received_by || user?.name || 'Admin',
          notes: formData.notes,
          newStatus: 'recorded',
          remainingBalance: 0
        });
        setShowReceipt(true);
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving payment:', error);
      toast.error(error.response?.data?.detail || (editingPayment ? 'Failed to update payment' : 'Failed to record payment'));
    }
  };

  const handlePrintReceipt = () => {
    const printContent = receiptRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Payment Receipt - ${receiptData?.billNumber || 'N/A'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 15px; }
            .header h1 { margin: 0; font-size: 20px; }
            .header p { margin: 5px 0 0; color: #666; font-size: 12px; }
            .receipt-number { text-align: center; background: #f5f5f5; padding: 8px; margin-bottom: 15px; font-family: monospace; }
            .row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #ddd; }
            .row:last-child { border-bottom: none; }
            .label { color: #666; font-size: 12px; }
            .value { font-weight: bold; font-size: 13px; }
            .amount-row { background: #e8f5e9; padding: 10px; margin: 15px 0; border-radius: 5px; }
            .amount-row .value { font-size: 18px; color: #2e7d32; }
            .footer { text-align: center; margin-top: 20px; padding-top: 15px; border-top: 2px solid #333; font-size: 11px; color: #666; }
            .status { display: inline-block; padding: 3px 10px; border-radius: 15px; font-size: 11px; font-weight: bold; }
            .status.paid { background: #c8e6c9; color: #2e7d32; }
            .status.partial { background: #fff3e0; color: #e65100; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handleEmailReceipt = async () => {
    if (!receiptData) return;
    
    // Get subscriber email
    const subscriberEmail = selectedSubscriber?.email;
    
    if (!subscriberEmail) {
      toast.error('Subscriber does not have an email address on file');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/email-receipt`, {
        subscriber_email: subscriberEmail,
        receipt_number: receiptData.receiptNumber,
        bill_number: receiptData.billNumber,
        subscriber_name: receiptData.subscriberName,
        subscriber_plan: receiptData.subscriberPlan,
        billing_month: receiptData.billingMonth,
        amount: receiptData.amount,
        payment_date: receiptData.paymentDate,
        payment_method: receiptData.paymentMethod,
        received_by: receiptData.receivedBy,
        status: receiptData.newStatus,
        remaining_balance: receiptData.remainingBalance || 0
      });
      
      toast.success(response.data.message);
      if (response.data.note) {
        toast.info(response.data.note, { duration: 5000 });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email receipt');
    } finally {
      setSendingEmail(false);
    }
  };

  const resetForm = () => {
    setFormData({
      subscriber_id: '',
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'cash',
      received_by: '',
      deposited_by: '',
      notes: '',
      bill_number: ''
    });
    setEditingPayment(null);
    setSubscriberSearch('');
    setSelectedSubscriber(null);
    setShowSubscriberDropdown(false);
    setUnpaidBills([]);
    setSelectedBill(null);
  };

  const handleSelectSubscriber = async (subscriber) => {
    setSelectedSubscriber(subscriber);
    setFormData({ ...formData, subscriber_id: subscriber.id, amount: '', bill_number: '' });
    setSubscriberSearch(subscriber.name);
    setShowSubscriberDropdown(false);
    setSelectedBill(null);
    
    // Fetch unpaid bills for this subscriber
    setLoadingBills(true);
    try {
      // First generate bills if needed
      await axios.post(`${API}/subscriber-bills/generate/${subscriber.id}?months=12`);
      
      // Then get unpaid bills
      const response = await axios.get(`${API}/subscriber-bills/${subscriber.id}`);
      const bills = response.data.filter(b => b.status !== 'paid');
      setUnpaidBills(bills);
      
      // Auto-select oldest unpaid bill
      if (bills.length > 0) {
        const oldestUnpaid = bills[bills.length - 1];
        setSelectedBill(oldestUnpaid);
        setFormData(prev => ({ 
          ...prev, 
          subscriber_id: subscriber.id,
          amount: oldestUnpaid.balance.toString(),
          bill_number: oldestUnpaid.bill_number
        }));
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
      // If bills endpoint fails, just set the subscriber amount
      setFormData(prev => ({ ...prev, amount: subscriber.amount || '' }));
    } finally {
      setLoadingBills(false);
    }
  };

  const handleSelectBill = (bill) => {
    setSelectedBill(bill);
    setFormData(prev => ({ 
      ...prev, 
      amount: bill.balance.toString(),
      bill_number: bill.bill_number,
      notes: `Payment for ${bill.bill_number} - ${bill.billing_month_name}`
    }));
  };

  const clearSubscriber = () => {
    setSelectedSubscriber(null);
    setFormData({ ...formData, subscriber_id: '', amount: '', bill_number: '' });
    setSubscriberSearch('');
    setUnpaidBills([]);
    setSelectedBill(null);
  };

  const filteredSubscribers = subscribers.filter(s => 
    s.name.toLowerCase().includes(subscriberSearch.toLowerCase()) ||
    s.mobile.includes(subscriberSearch) ||
    s.plan?.toLowerCase().includes(subscriberSearch.toLowerCase())
  );

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/payments/import/excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import payments');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/payments/template/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'payments_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      subscriber_id: payment.subscriber_id,
      amount: payment.amount,
      payment_date: payment.payment_date.split('T')[0],
      payment_method: payment.payment_method,
      received_by: payment.received_by || '',
      deposited_by: payment.deposited_by || '',
      notes: payment.notes || ''
    });
    // Set the subscriber search field
    const sub = subscribers.find(s => s.id === payment.subscriber_id);
    if (sub) {
      setSelectedSubscriber(sub);
      setSubscriberSearch(sub.name);
    }
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await axios.delete(`${API}/payments/${id}`);
        toast.success('Payment deleted successfully');
        fetchData();
      } catch (error) {
        console.error('Error deleting payment:', error);
        toast.error('Failed to delete payment');
      }
    }
  };

  const handleConfirmDeposit = async (paymentId) => {
    if (window.confirm('Are you sure you want to confirm this payment has been deposited to the bank?')) {
      try {
        await axios.put(`${API}/payments/${paymentId}/confirm-deposit`);
        toast.success('Deposit confirmed successfully!');
        fetchData();
      } catch (error) {
        console.error('Error confirming deposit:', error);
        toast.error(error.response?.data?.detail || 'Failed to confirm deposit');
      }
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/payments/export/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payments_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payments exported successfully!');
    } catch (error) {
      toast.error('Failed to export payments');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Filter payments by search term
  const filteredPayments = payments.filter(payment => 
    payment.subscriber_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Hidden file input for import */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImportExcel}
        accept=".xlsx,.xls"
        className="hidden"
      />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1 w-full sm:w-auto sm:max-w-xs">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <Input
              type="text"
              placeholder="Search by subscriber name..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            onClick={handleDownloadTemplate}
            title="Download import template"
          >
            <FileSpreadsheet size={20} className="mr-2" />
            Template
          </Button>
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
          >
            <Upload size={20} className="mr-2" />
            {importing ? 'Importing...' : 'Import'}
          </Button>
          <Button 
            variant="outline" 
            onClick={handleExportExcel}
            disabled={exporting || payments.length === 0}
          >
            <Download size={20} className="mr-2" />
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="record-payment-btn">
                <Plus size={20} className="mr-2" />
                Record Payment
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="payment-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPayment ? 'Edit Payment' : 'Record New Payment'}</DialogTitle>
              <DialogDescription>
                {editingPayment ? 'Update payment information.' : 'Record a payment received from a subscriber.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label htmlFor="subscriber" className="text-sm">Subscriber *</Label>
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                      <Input
                        type="text"
                        value={subscriberSearch}
                        onChange={(e) => {
                          setSubscriberSearch(e.target.value);
                          setShowSubscriberDropdown(true);
                          if (!e.target.value) {
                            setSelectedSubscriber(null);
                            setFormData({ ...formData, subscriber_id: '', amount: '' });
                          }
                        }}
                        onFocus={() => setShowSubscriberDropdown(true)}
                        placeholder="Type to search subscriber..."
                        className="h-9 pl-9 pr-8"
                        data-testid="payment-subscriber-search"
                      />
                      {selectedSubscriber && (
                        <button
                          type="button"
                          onClick={clearSubscriber}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    {showSubscriberDropdown && subscriberSearch && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredSubscribers.length > 0 ? (
                          filteredSubscribers.map((subscriber) => (
                            <button
                              key={subscriber.id}
                              type="button"
                              onClick={() => handleSelectSubscriber(subscriber)}
                              className={`w-full text-left px-3 py-2 hover:bg-blue-50 flex justify-between items-center ${
                                selectedSubscriber?.id === subscriber.id ? 'bg-blue-50 text-blue-700' : ''
                              }`}
                            >
                              <div>
                                <p className="font-medium text-slate-900">{subscriber.name}</p>
                                <p className="text-xs text-slate-500">{subscriber.mobile} - {subscriber.plan}</p>
                              </div>
                              <span className="text-sm font-medium text-green-600">₱{subscriber.amount}</span>
                            </button>
                          ))
                        ) : (
                          <p className="px-3 py-2 text-slate-500 text-sm">No subscribers found</p>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedSubscriber && (
                    <p className="text-xs text-green-600 mt-1">
                      Selected: {selectedSubscriber.name} - {selectedSubscriber.plan}
                    </p>
                  )}
                </div>
                
                {/* Unpaid Bills Section */}
                {selectedSubscriber && (
                  <div className="col-span-2">
                    <Label className="text-sm flex items-center gap-2">
                      <Receipt size={14} />
                      Select Bill to Pay
                    </Label>
                    {loadingBills ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-600"></div>
                        <span className="ml-2 text-sm text-slate-500">Loading bills...</span>
                      </div>
                    ) : unpaidBills.length > 0 ? (
                      <div className="space-y-2 mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2">
                        {unpaidBills.map((bill) => (
                          <div
                            key={bill.id}
                            onClick={() => handleSelectBill(bill)}
                            className={`p-2 rounded-lg border cursor-pointer transition-colors ${
                              selectedBill?.id === bill.id
                                ? 'bg-emerald-50 border-emerald-300'
                                : 'bg-white border-slate-200 hover:border-emerald-200'
                            }`}
                            data-testid={`bill-option-${bill.bill_number}`}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-mono text-xs font-semibold text-slate-700">{bill.bill_number}</p>
                                <p className="text-xs text-slate-500">{bill.billing_month_name}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-red-600 text-sm">₱{bill.balance?.toLocaleString()}</p>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  bill.status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {bill.status === 'partial' ? 'PARTIAL' : 'UNPAID'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-3 text-slate-500 border border-slate-200 rounded-lg mt-2">
                        <CheckCircle size={20} className="mx-auto mb-1 text-green-500" />
                        <p className="text-xs">No unpaid bills! All caught up.</p>
                      </div>
                    )}
                    {selectedBill && (
                      <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                        <AlertCircle size={12} />
                        Paying: {selectedBill.bill_number} - {selectedBill.billing_month_name}
                      </p>
                    )}
                  </div>
                )}
                
                <div>
                  <Label htmlFor="amount" className="text-sm">Amount (PHP) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                    className="h-9"
                    data-testid="payment-amount-input"
                  />
                </div>
                <div>
                  <Label htmlFor="payment_date" className="text-sm">Payment Date *</Label>
                  <Input
                    id="payment_date"
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                    required
                    className="h-9"
                    data-testid="payment-date-input"
                  />
                </div>
                <div>
                  <Label htmlFor="payment_method" className="text-sm">Payment Method *</Label>
                  <Select value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })}>
                    <SelectTrigger data-testid="payment-method-select" className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="gcash">GCash</SelectItem>
                      <SelectItem value="paymaya">PayMaya</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="received_by" className="text-sm">Received By</Label>
                  <Input
                    id="received_by"
                    value={formData.received_by}
                    onChange={(e) => setFormData({ ...formData, received_by: e.target.value })}
                    placeholder="Staff name"
                    className="h-9"
                    data-testid="payment-received-by-input"
                  />
                </div>
                <div>
                  <Label htmlFor="deposited_by" className="text-sm">Deposited By</Label>
                  <Input
                    id="deposited_by"
                    value={formData.deposited_by}
                    onChange={(e) => setFormData({ ...formData, deposited_by: e.target.value })}
                    placeholder="Staff name"
                    className="h-9"
                    data-testid="payment-deposited-by-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="notes" className="text-sm">Notes</Label>
                  <Input
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Optional notes"
                    className="h-9"
                    data-testid="payment-notes-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-payment-btn">
                {editingPayment ? 'Update Payment' : 'Record Payment'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Payments List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="payments-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Subscriber</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Amount</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Method</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Received By</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Deposit Status</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Notes</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPayments.length > 0 ? (
                filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-slate-50 transition-colors" data-testid={`payment-row-${payment.id}`}>
                    <td className="px-6 py-4 text-slate-700">{new Date(payment.payment_date).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900">{payment.subscriber_name}</p>
                    </td>
                    <td className="px-6 py-4 font-bold text-green-600">₱{payment.amount.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium border border-blue-200">
                        {payment.payment_method}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-700">{payment.received_by || '-'}</td>
                    <td className="px-6 py-4">
                      {payment.deposit_confirmed ? (
                        <div>
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium border border-emerald-200">
                            <CheckCircle size={12} />
                            Confirmed
                          </span>
                          <p className="text-xs text-slate-500 mt-1">
                            By: {payment.deposit_confirmed_by}
                          </p>
                          {payment.deposit_confirmed_at && (
                            <p className="text-xs text-slate-400">
                              {new Date(payment.deposit_confirmed_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium border border-amber-200">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-600 text-sm">{payment.notes || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1">
                        {/* Confirm Deposit button - only for Alma or Admin, and only if not confirmed */}
                        {canConfirmDeposit && !payment.deposit_confirmed && (
                          <button
                            onClick={() => handleConfirmDeposit(payment.id)}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Confirm deposit to bank"
                            data-testid={`confirm-deposit-${payment.id}`}
                          >
                            <BanknoteIcon size={18} />
                          </button>
                        )}
                        {canEditPayments && (
                          <button
                            onClick={() => handleEdit(payment)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit payment"
                            data-testid={`edit-payment-${payment.id}`}
                          >
                            <Edit size={18} />
                          </button>
                        )}
                        {canDeletePayments && (
                          <button
                            onClick={() => handleDelete(payment.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete payment"
                            data-testid={`delete-payment-${payment.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-slate-500">
                    {searchTerm ? 'No payments found matching your search' : 'No payments recorded yet'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Receipt Dialog */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="max-w-md" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt size={20} className="text-green-600" />
              Payment Receipt
            </DialogTitle>
            <DialogDescription>
              Payment recorded successfully
            </DialogDescription>
          </DialogHeader>
          
          {receiptData && (
            <div ref={receiptRef} className="bg-white">
              {/* Receipt Content for Print */}
              <div className="header text-center border-b-2 border-slate-800 pb-3 mb-4">
                <h1 className="text-xl font-bold text-slate-800">PAYMENT RECEIPT</h1>
                <p className="text-sm text-slate-500 mt-1">Mipay Subscription Services</p>
              </div>
              
              <div className="receipt-number text-center bg-slate-100 p-2 rounded font-mono text-sm mb-4">
                {receiptData.receiptNumber}
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Bill Number</span>
                  <span className="font-semibold font-mono">{receiptData.billNumber}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Subscriber</span>
                  <span className="font-semibold">{receiptData.subscriberName}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Plan</span>
                  <span className="font-semibold">{receiptData.subscriberPlan}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Billing Month</span>
                  <span className="font-semibold">{receiptData.billingMonth}</span>
                </div>
                
                <div className="bg-green-50 p-3 rounded-lg my-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 text-sm">Amount Paid</span>
                    <span className="text-2xl font-bold text-green-700">₱{receiptData.amount?.toLocaleString()}</span>
                  </div>
                </div>
                
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Payment Date</span>
                  <span className="font-semibold">{new Date(receiptData.paymentDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Payment Method</span>
                  <span className="font-semibold capitalize">{receiptData.paymentMethod}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Received By</span>
                  <span className="font-semibold">{receiptData.receivedBy}</span>
                </div>
                <div className="flex justify-between py-1 items-center">
                  <span className="text-slate-500 text-sm">Status</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    receiptData.newStatus === 'paid' ? 'bg-green-100 text-green-700' : 
                    receiptData.newStatus === 'partial' ? 'bg-amber-100 text-amber-700' : 
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {receiptData.newStatus?.toUpperCase()}
                  </span>
                </div>
                {receiptData.remainingBalance > 0 && (
                  <div className="flex justify-between py-1 bg-amber-50 px-2 rounded">
                    <span className="text-amber-700 text-sm">Remaining Balance</span>
                    <span className="font-bold text-amber-700">₱{receiptData.remainingBalance?.toLocaleString()}</span>
                  </div>
                )}
                {receiptData.notes && (
                  <div className="pt-2 border-t border-slate-200 mt-2">
                    <span className="text-slate-500 text-xs">Notes: </span>
                    <span className="text-sm">{receiptData.notes}</span>
                  </div>
                )}
              </div>
              
              <div className="footer text-center mt-4 pt-3 border-t-2 border-slate-800 text-xs text-slate-500">
                <p>Thank you for your payment!</p>
                <p className="mt-1">Generated: {new Date().toLocaleString()}</p>
              </div>
            </div>
          )}
          
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowReceipt(false)}
              className="flex-1"
            >
              Close
            </Button>
            {selectedSubscriber?.email && (
              <Button
                onClick={handleEmailReceipt}
                disabled={sendingEmail}
                variant="outline"
                className="flex-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                data-testid="email-receipt-btn"
              >
                {sendingEmail ? (
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                ) : (
                  <Mail size={16} className="mr-2" />
                )}
                {sendingEmail ? 'Sending...' : 'Email'}
              </Button>
            )}
            <Button
              onClick={handlePrintReceipt}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid="print-receipt-btn"
            >
              <Printer size={16} className="mr-2" />
              Print
            </Button>
          </div>
          {!selectedSubscriber?.email && (
            <p className="text-xs text-slate-500 text-center mt-2">
              No email on file for this subscriber
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payments;